import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangleIcon, ArrowLeftIcon } from 'lucide-react';
import { DetailFieldSection } from '../components/DetailFieldSection';
import { ConditionsPanel } from '../components/ConditionsPanel';
import { SlackChannelPanel } from '../components/SlackChannelPanel';
import { loans } from '../data/loanData';
import { loanDetails, type DetailField, type LoanDetailSections } from '../data/loanDetails';
import { formatCurrency } from '../data/mockData';
const indicatorValues = ['green', 'amber', 'red'] as const;

/** Apply an edit to whichever column holds the field. */
function applyEdit(fields: DetailField[], label: string, value: string): DetailField[] {
  return fields.map(field => {
    if (field.label !== label) return field;
    if (field.indicator) {
      const next = indicatorValues.find(option => option === value);
      return next ? {
        ...field,
        indicator: next
      } : field;
    }
    return {
      ...field,
      value: value || undefined
    };
  });
}
const tabs = ['Overview', 'Conditions & docs', 'Slack channel'] as const;
type Tab = (typeof tabs)[number];
export function LoanDetail() {
  const {
    loanId
  } = useParams();
  const [tab, setTab] = useState<Tab>('Overview');
  const loan = loans.find(item => item.id === loanId);
  const [details, setDetails] = useState<LoanDetailSections | undefined>(() => loanId ? loanDetails[loanId] : undefined);
  const [savedLabel, setSavedLabel] = useState<string | null>(null);
  useEffect(() => {
    setDetails(loanId ? loanDetails[loanId] : undefined);
  }, [loanId]);
  useEffect(() => {
    if (!savedLabel) return;
    const timer = window.setTimeout(() => setSavedLabel(null), 2000);
    return () => window.clearTimeout(timer);
  }, [savedLabel]);
  const editField = (section: 'information' | 'team') => (label: string, value: string) => {
    setDetails(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [section]: {
          left: applyEdit(prev[section].left, label, value),
          right: applyEdit(prev[section].right, label, value)
        }
      };
    });
    setSavedLabel(label);
  };
  if (!loan) {
    return <div className="rounded-xl border border-gray-200 bg-white px-5 py-12 text-center" data-id="element-849" data-mp="1nmzwbfnvg2wa">
        <p className="text-[14px] font-semibold text-ink" data-id="element-850" data-mp="1slb5do6ce9dg">Loan not found</p>
        <Link to="/loans" className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-brand hover:text-brand-dark" data-id="element-851" data-mp="1dotsutmf2opb">
          <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-852" data-mp="1im25keosq0j6" />
          Back to loans
        </Link>
      </div>;
  }
  const outstanding = loan.conditions.filter(c => c.status === 'Outstanding').length;
  const matched = loan.conditions.filter(c => c.status === 'AI Matched').length;
  const cleared = loan.conditions.filter(c => c.status === 'Accepted').length;
  return <div className="space-y-4" data-id="element-853" data-mp="33zonsdibtwm3">
      <Link to="/loans" className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-gray-500 transition-colors hover:text-ink" data-id="element-854" data-mp="3e56gngwduaa9">
        <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-855" data-mp="394ii8a5tqr60" />
        Loans in process
      </Link>

      <header className="rounded-xl border border-gray-200 bg-white" data-id="element-856" data-mp="3mzun3adp1y0n">
        <div className="flex flex-wrap items-start gap-4 px-5 py-4" data-id="element-857" data-mp="3i5qfqq0innz1">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-navy text-[14px] font-bold text-white" data-id="element-858" data-mp="q0ayanpjfng">
            {loan.borrower.split(' ').map(part => part[0]).slice(0, 2).join('')}
          </span>
          <div className="min-w-0" data-id="element-859" data-mp="5pmqiqr0d7zf">
            <div className="flex flex-wrap items-center gap-2" data-id="element-860" data-mp="5qo34znoa2j9">
              <h1 className="text-[22px] font-bold text-ink" data-id="element-861" data-mp="2bi2zz65kpphe">{loan.borrower}</h1>
              <span className="rounded-full bg-navy-soft px-2.5 py-1 text-[11px] font-medium text-navy" data-id="element-862" data-mp="27hj4e70netxd">
                {loan.stage}
              </span>
              {loan.stalled && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-medium text-amber-700" data-id="element-863" data-mp="2dxhwjdbs2ffo">
                  <AlertTriangleIcon className="h-3 w-3" aria-hidden="true" data-id="element-864" data-mp="2i0egb7d20hl7" />
                  Stalled
                </span>}
            </div>
            <p className="mt-1 text-[13px] text-gray-500" data-id="element-865" data-mp="1q870281gn77c">
              {loan.number} · {loan.state} · {formatCurrency(loan.amount)}
            </p>
          </div>

          <dl className="ml-auto flex flex-wrap gap-x-8 gap-y-2" data-id="element-866" data-mp="1u7fu7e0ex73f">
            <div data-id="element-867" data-mp="1z7k9j0pl3vs2">
              <dt className="text-[11px] uppercase tracking-wide text-gray-400" data-id="element-868" data-mp="1z724beky2780">
                Processor
              </dt>
              <dd className="text-[13px] text-ink" data-id="element-869" data-mp="2416hifkszbhj">{loan.processor}</dd>
            </div>
            <div data-id="element-870" data-mp="16hv8wogsdxqz">
              <dt className="text-[11px] uppercase tracking-wide text-gray-400" data-id="element-871" data-mp="1bj12eoi9tp1k">
                Target closing
              </dt>
              <dd className="text-[13px] text-ink" data-id="element-872" data-mp="13zrvr315g4g7">{loan.closingDate}</dd>
            </div>
            <div data-id="element-873" data-mp="yzx0yoe96upv">
              <dt className="text-[11px] uppercase tracking-wide text-gray-400" data-id="element-874" data-mp="z1gi0h7qh9sp">
                Days in stage
              </dt>
              <dd className="text-[13px] text-ink" data-id="element-875" data-mp="u3f2g69inslz">{loan.daysInStage}</dd>
            </div>
            <div data-id="element-876" data-mp="pzh3bvk6yhsx">
              <dt className="text-[11px] uppercase tracking-wide text-gray-400" data-id="element-877" data-mp="q238jojm0l7z">
                Conditions
              </dt>
              <dd className="text-[13px] text-ink" data-id="element-878" data-mp="g9fdozc6wrbw">
                {outstanding} out · {matched} to review · {cleared} cleared
              </dd>
            </div>
          </dl>
        </div>

        <div className="flex gap-1 border-t border-gray-200 px-3" role="tablist" data-id="element-879" data-mp="6c18jvvpvoyw">
          {tabs.map(item => <button key={item} type="button" role="tab" aria-selected={tab === item} onClick={() => setTab(item)} className={['-mb-px border-b-2 px-3 py-2.5 text-[13px] font-medium transition-colors', tab === item ? 'border-brand text-brand' : 'border-transparent text-gray-500 hover:text-ink'].join(' ')} data-id="element-880" data-mp="2afpfsslaeg9">
              {item}
            </button>)}
        </div>
      </header>

      {tab === 'Overview' && details && <div className="space-y-4" data-id="element-881" data-mp="6f02ya7bh867">
          <DetailFieldSection title="Loan Information" left={details.information.left} right={details.information.right} onChange={editField('information')} data-id="element-882" data-mp="6g1ox9pvff2p" />
          <DetailFieldSection title="Loan Team" left={details.team.left} right={details.team.right} onChange={editField('team')} data-id="element-883" data-mp="1n8lqqa1wesq" />
          <p className="h-4 text-[12px] text-success" role="status" data-id="element-884" data-mp="pazx9rnronsy">
            {savedLabel ? `${savedLabel} saved` : ''}
          </p>
        </div>}

      {tab === 'Overview' && !details && <p className="rounded-xl border border-dashed border-gray-200 px-5 py-10 text-center text-[12.5px] text-gray-500" data-id="element-885" data-mp="2erlq9uzk4ocv">
          No detail record synced for this loan yet.
        </p>}

      {tab === 'Conditions & docs' && <ConditionsPanel conditions={loan.conditions} data-id="element-886" data-mp="2jk54mu62jniq" />}

      {tab === 'Slack channel' && <SlackChannelPanel loanId={loan.id} loanNumber={loan.number} data-id="element-887" data-mp="1vukhs06so646" />}
    </div>;
}